import {isPlatformBrowser, NgForOf, NgIf} from '@angular/common';
import {AfterViewInit, Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {FaqSectionComponent} from '../../ui/faq-section/faq-section.component';
import {PublicTripCalculatorComponent} from '../../ui/public-trip-calculator/public-trip-calculator.component';
import {LANDING_PAGES} from "../../../constants/landing-pages";
import {APP_ROUTES} from "../../../constants/routes";
import {LandingAction, LandingPageDefinition} from "../../../interfaces/landing-page";
import {Template} from "../../../interfaces/template";
import {AosService} from "../../../services/aos.service";
import {SeoBreadcrumbItem, SeoService} from "../../../services/seo.service";
import {AnalyticsService} from "../../../services/analytics.service";
import {RoutePlannerComponent} from '../../ui/route-planner/route-planner.component';
import {PublicFuelCalculatorComponent} from '../../ui/public-fuel-calculator/public-fuel-calculator.component';

@Component({
    selector: 'app-content-page',
    standalone: true,
    imports: [
        RouterLink,
        NgIf,
        NgForOf,
        FaqSectionComponent,
        PublicTripCalculatorComponent,
        RoutePlannerComponent,
        PublicFuelCalculatorComponent,
    ],
    templateUrl: './content-page.component.html',
    styleUrl: './content-page.component.scss',
})
export class ContentPageComponent implements OnInit, AfterViewInit {
    template!: Template;
    page!: LandingPageDefinition;
    breadcrumbTrail: SeoBreadcrumbItem[] = [];

    constructor(
        private route: ActivatedRoute,
        private aosService: AosService,
        private seoService: SeoService,
        private analytics: AnalyticsService,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {
    }

    ngOnInit(): void {
        const pageKey = this.route.snapshot.data['pageKey'];
        this.page = LANDING_PAGES[pageKey];
        this.template = new Template(this.route.snapshot.data['content']);
        this.breadcrumbTrail = this.buildBreadcrumbs();

        if (this.template && this.page) {
            this.seoService.applyTemplate(this.template, {
                path: this.page.path,
                image: this.page.image.src,
                imageAlt: this.page.image.alt,
                faq: this.page.faq,
                applicationCategory: this.page.applicationCategory,
                howTo: this.page.howTo,
                breadcrumb: this.breadcrumbTrail,
            });
        }
    }

    ngAfterViewInit(): void {
        void this.aosService.refresh();
        this.scrollToRouteFragment();
    }

    linkIsExternal(action: LandingAction | undefined): boolean {
        return !!action?.external;
    }

    internalActionPath(action: LandingAction): string {
        return action.url.split('#', 1)[0];
    }

    internalActionFragment(action: LandingAction): string | undefined {
        const fragment = action.url.split('#', 2)[1];
        return fragment || undefined;
    }

    externalActionUrl(action: LandingAction, placement: string): string {
        return this.analytics.externalLkUrl(action.url, `${this.page.key}_${placement}`);
    }

    trackExternalAction(action: LandingAction, placement: string): void {
        this.analytics.trackExternalAction(
            action.url,
            `${this.page.key}_${placement}`,
            action.label,
        );
    }

    scrollToNextScreen(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const target = document.getElementById('page-content');
        if (!target) {
            return;
        }

        const top = target.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({
            top: Math.max(top, 0),
            behavior: 'smooth',
        });
    }

    getAosDelay(index: number, step = 90, max = 270): string {
        return `${Math.min(index * step, max)}`;
    }

    private scrollToRouteFragment(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const fragment = this.route.snapshot.fragment;
        if (!fragment) {
            return;
        }

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                const target = document.getElementById(fragment);
                if (!target) {
                    return;
                }

                const top = target.getBoundingClientRect().top + window.scrollY - 96;
                window.scrollTo({top: Math.max(top, 0), behavior: 'auto'});
            });
        });
    }

    private buildBreadcrumbs(): SeoBreadcrumbItem[] {
        if (!this.page || this.page.path === APP_ROUTES.INDEX) {
            return [
                {name: 'Главная', path: APP_ROUTES.INDEX},
            ];
        }

        if (this.page.path.startsWith(`${APP_ROUTES.FEATURES.INDEX}/`)) {
            return [
                {name: 'Главная', path: APP_ROUTES.INDEX},
                {name: LANDING_PAGES['features'].navLabel, path: APP_ROUTES.FEATURES.INDEX},
                {name: this.template?.data?.page?.breadcrumb || this.page.navLabel, path: this.page.path},
            ];
        }

        if (this.page.path.startsWith(`${APP_ROUTES.TOOLS.INDEX}/`)) {
            return [
                {name: 'Главная', path: APP_ROUTES.INDEX},
                {name: LANDING_PAGES['tools'].navLabel, path: APP_ROUTES.TOOLS.INDEX},
                {name: this.template?.data?.page?.breadcrumb || this.page.navLabel, path: this.page.path},
            ];
        }

        return [
            {name: 'Главная', path: APP_ROUTES.INDEX},
            {name: this.template?.data?.page?.breadcrumb || this.page.navLabel, path: this.page.path},
        ];
    }

    protected readonly APP_ROUTES = APP_ROUTES;
}
